import type { ComputedBeneficiaryListItemPayload } from '@Application/Beneficiaries/Beneficiaries/Actions/beneficiariesFetched';
import type PlainDate from '@Domain/PlainDate';
import isPurgeApproaching from '@UI/Store/Beneficiaries/Views/isPurgeApproaching';
import LabeledValueView from '@UI/Store/Shared/Views/LabeledValueView';

/**
 * Immutable View: `readonly` properties, frozen at the end of the constructor.
 * Carries the list-item shape only: the server never sends `nationalHealthIdentifier` on the list
 * route, so this View does not declare the field — see `BeneficiaryView` for the detail shape.
 */
class BeneficiaryListItemView {
	readonly id: number;
	readonly firstName: string;
	readonly lastName: string;
	readonly birthName: string | null;
	readonly birthDate: PlainDate | null;
	readonly homeAddress: string | null;
	readonly phoneNumber: string | null;
	readonly status: LabeledValueView;
	readonly archivedAt: Date | null;
	readonly purgeScheduledAt: Date | null;
	readonly facilityId: number;
	readonly assignedUserIds: readonly number[];
	readonly interviewsCount: number;
	readonly documentsCount: number;

	constructor(payload: ComputedBeneficiaryListItemPayload) {
		this.id = payload.id;
		this.firstName = payload.firstName;
		this.lastName = payload.lastName;
		this.birthName = payload.birthName;
		this.birthDate = payload.birthDate;
		this.homeAddress = payload.homeAddress;
		this.phoneNumber = payload.phoneNumber;
		this.status = new LabeledValueView(payload.status);
		this.archivedAt = payload.archivedAt;
		this.purgeScheduledAt = payload.purgeScheduledAt;
		this.facilityId = payload.facility.id;
		this.assignedUserIds = payload.assignedUsers.map((assignedUser) => assignedUser.id);
		this.interviewsCount = payload.interviewsCount;
		this.documentsCount = payload.documentsCount;

		Object.freeze(this);
	}

	/**
	 * Surname first, French administrative order — the identity of a care record, not a contact card.
	 * The surname's uppercasing is a *display* concern (`BeneficiaryName`, `text-transform`): applying
	 * it here would put it in the DOM, where a screen reader spells it letter by letter and a copy
	 * carries it into a letter. Casing is never normalized on input either — `de La Fontaine`,
	 * `McDonald` and `d'Arcy` are correct as typed, and a mangled name on a health record is an
	 * identity error.
	 */
	get fullName(): string {
		return `${this.lastName} ${this.firstName}`;
	}

	/**
	 * The birth name, only when it differs from the name in use. It is the reference identity trait
	 * of a care record: collected on the form, it has to be readable somewhere.
	 */
	get birthNameMention(): string | null {
		if (this.birthName === null || this.birthName === this.lastName) {
			return null;
		}

		return `né(e) ${this.birthName}`;
	}

	get isArchived(): boolean {
		return this.status.value === 'archived';
	}

	get isPurgeApproaching(): boolean {
		return isPurgeApproaching(this.purgeScheduledAt);
	}
}

export default BeneficiaryListItemView;
